import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, Input } from '@angular/core';
import jsQR from 'jsqr';

import { BarcodeFormat, DecodeHintType } from '@zxing/library';
import { BrowserMultiFormatReader, IScannerControls } from '@zxing/browser';
import { CardsService } from '../../services/cards.service';
import { ActivatedRoute } from '@angular/router';
import { CamapignService } from '../../services/campaign.service';
import { CamapignScanService } from '../../services/campaign-scan.service';
import { Camapign, CamapignScanDetails } from '../../interfaces/campaigns.interface';
import { PartnerProductsService } from '../../services/partner-products.service';
import { PartnerProduct } from '../../interfaces/partner-product.interface';
import { environment } from '../../../environments/environment';
import { CameraService } from '../../services/camera.service';

@Component({
  selector: 'app-qr-scanner-component',
  standalone: false,
  templateUrl: './qr-scanner-component.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./qr-scanner-component.component.scss']
})
export class QrScannerComponentComponent implements OnInit, OnDestroy {

  @ViewChild('video', { static: true }) videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas', { static: true }) canvasElement!: ElementRef<HTMLCanvasElement>;

  @Input() campaign_id?: number | null;

  scannedCode: string | null = null;
  scanning = true;
  cameraError: string | null = null;

  mode: 'card' | 'preview' = 'card';

  // Manual code entry
  manualEntryMode = false;
  manualCode = '';

  scannedResult: any = null;

  campaign: Camapign | null = null;
  campaignLoading = false;
  campaignError: string | null = null;
  campaignId: number | null = null;

  scanDetails: CamapignScanDetails | null = null;
  scanDetailsLoading = false;
  scanDetailsError: string | null = null;
  scanDetailsErrors: string[] = [];

  // Entry popup
  entryPopupVisible = false;
  entryCountdown = 3;
  entryProcessing = false;
  entrySuccess = false;
  entryError: string | null = null;
  private entryCountdownInterval: any = null;

  imagesUrl = environment.ImagesUrl;

  // Reward selection
  rewardSelectionVisible = false;
  rewards: PartnerProduct[] = [];
  rewardsLoading = false;
  rewardsError: string | null = null;

  // Product registration popup
  selectedProduct: PartnerProduct | null = null;
  productPopupVisible = false;
  productCountdown = 3;
  productProcessing = false;
  productSuccess = false;
  productError: string | null = null;
  private productCountdownInterval: any = null;

  // Transaction form + popup
  transactionFormVisible = false;
  transactionAmount: number | null = null;
  transactionPopupVisible = false;
  transactionCountdown = 3;
  transactionProcessing = false;
  transactionSuccess = false;
  transactionError: string | null = null;
  transactionErrorDetail: string | null = null;
  private transactionCountdownInterval: any = null;

  private codeReader: BrowserMultiFormatReader | null = null;
  private stream: MediaStream | null = null;
  private scanTimer: any;

  private controls: IScannerControls | null = null;

  constructor(
    private http: HttpClient,
    private cardService: CardsService,
    private route: ActivatedRoute,
    private campaignService: CamapignService,
    private campaignScanService: CamapignScanService,
    private partnerProductsService: PartnerProductsService,
    private cameraService: CameraService
  ) { }

  async ngOnInit() {

    // Check if campaign_id is provided via @Input, otherwise read from route
    if (this.campaign_id) {
      this.campaignId = this.campaign_id;
      this.loadCampaign(this.campaign_id);
    } else {
      this.route.paramMap.subscribe(params => {
        const campaign_id = params.get('campaign_id');
        if (campaign_id) {
          this.campaignId = +campaign_id;
          this.loadCampaign(+campaign_id);
        }
      });
    }



    navigator?.vibrate(0);


    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.QR_CODE,
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.CODE_128,
      BarcodeFormat.CODE_39,
      BarcodeFormat.CODE_93,
      BarcodeFormat.UPC_A,
      BarcodeFormat.UPC_E,
      BarcodeFormat.ITF,
      BarcodeFormat.DATA_MATRIX,
      BarcodeFormat.AZTEC,
      BarcodeFormat.PDF_417,
      BarcodeFormat.CODABAR,
      BarcodeFormat.UPC_EAN_EXTENSION
    ]);

    this.codeReader = new BrowserMultiFormatReader(hints);

    this.startScanLoop();
  }

  private loadCampaign(campaignId: number): void {
    this.campaignLoading = true;
    this.campaignError = null;
    this.campaignService.getCamapignById(campaignId).subscribe({
      next: (c: Camapign) => {
        this.campaign = c;
        this.campaignLoading = false;
      },
      error: () => {
        this.campaignError = 'Nie udało się załadować kampanii.';
        this.campaignLoading = false;
      }
    });
  }

  ngOnDestroy() {
    this.controls?.stop();
    this.stopStream();

    this.scanning = false;

    clearInterval(this.scanTimer);
    clearInterval(this.entryCountdownInterval);
    clearInterval(this.transactionCountdownInterval);
    clearInterval(this.productCountdownInterval);
    //this.codeReader?.reset();
    this.codeReader = null as any;
  }


  private stopStream() {
    if (this.stream) {
      this.stream.getTracks().forEach(t => t.stop());
      this.stream = null;
    }
  }

  private async startScanLoop() {
    const video = this.videoElement.nativeElement;
    this.cameraError = null;

    // Check camera support and handle iOS WKWebView
    const supportInfo = this.cameraService.getCameraSupportInfo();
    
    if (supportInfo.isIOSWebView) {
      this.cameraError = 'Kamera nie jest dostępna w tym trybie. Otwórz aplikację w Safari lub użyj natywnej aplikacji.';
      console.error('Camera not available in iOS WKWebView');
      return;
    }

    if (!supportInfo.isSecure) {
      this.cameraError = 'Kamera wymaga połączenia HTTPS. Otwórz stronę przez https://';
      console.error('getUserMedia requires a secure context (HTTPS).');
      return;
    }

    if (!supportInfo.isSupported) {
      this.cameraError = 'Twoja przeglądarka nie obsługuje dostępu do kamery.';
      console.error('Camera is not supported on this platform.');
      return;
    }

    try {
      // Check permissions first
      const hasPermission = await this.cameraService.checkCameraSupport();
      if (!hasPermission) {
        this.cameraError = 'Brak uprawnień do kamery. Zezwól na dostęp do kamery w ustawieniach.';
        return;
      }

      // Get camera stream using the service
      this.stream = await this.cameraService.getCameraStream({
        video: { facingMode: { ideal: 'environment' } }
      });

      // Manually set up the video element
      // Firefox requires srcObject, muted and playsInline to be set as
      // properties (not only HTML attributes) and video.play() to be
      // awaited before any frame-reading begins.
      video.srcObject = this.stream;
      video.muted = true;
      (video as any).playsInline = true;
      await video.play();

      // Hand the already-playing video element to zxing – it will only
      // read frames and won't touch the stream itself.
      this.controls = await this.codeReader!.decodeFromVideoElement(
        video,
        (result, _error, controls) => {
          if (result) {
            this.scannedCode = result.getText();
            this.scanning = false;
            controls.stop();
            this.stopStream();
            this.sendToBackend(this.scannedCode!);
          }
          // errors (no code found in frame) are intentionally ignored
        }
      );
    } catch (err: any) {
      console.error('Błąd dostępu do kamery:', err);
      this.cameraError = err?.message || `Nie można uruchomić kamery: ${err}`;
    }
  }

  private sendToBackend(code: string) {
    if (this.campaignId) {
      this.scanDetailsLoading = true;
      this.scanDetailsError = null;
      this.scanDetailsErrors = [];
      this.scanDetails = null;
      this.campaignScanService.getScanDetails({ campaign_id: this.campaignId, code }).subscribe({
        next: (details: CamapignScanDetails) => {
          this.scanDetails = details;
          this.scanDetailsLoading = false;
          navigator?.vibrate([200, 100, 200, 200, 400, 100, 400]);
        },
        error: (err: any) => {
          const body = err?.error;
          const status = err?.status;
          const txtError = err?.detail;

          if (status === 400) {
            this.scanDetailsErrors = ['Błąd skanowanie. Karta nie jest jest aktywna']
          } else if (Array.isArray(body?.errors)) {
            this.scanDetailsErrors = body.errors.map((e: any) => typeof e === 'string' ? e : (e?.message ?? JSON.stringify(e)));
          } else if (Array.isArray(body?.messages)) {
            this.scanDetailsErrors = body.messages;
          } else if (body?.message) {
            this.scanDetailsErrors = [body.message];
          } else if (body?.error && typeof body.error === 'string') {
            this.scanDetailsErrors = [body.error];
          } else if (err?.message) {
            this.scanDetailsErrors = [err.message];
          } else {
            this.scanDetailsErrors = ['Wystąpił błąd podczas przetwarzania skanu.'];
          }
          this.scanDetailsError = this.scanDetailsErrors[0] ?? null;
          this.scanDetailsLoading = false;
          navigator?.vibrate([500, 100, 500]);
        }
      });
    } else {
      this.cardService.checkCardValidity(code).subscribe(result => {
        this.scannedResult = result;
        navigator?.vibrate([200, 100, 200, 200, 400, 100, 400]);
      });
    }
  }

  restartScan() {
    // Stop previous scan and camera
    this.controls?.stop();
    this.controls = null;
    this.stopStream();
    this.scannedCode = null;
    this.scanning = true;
    this.scanDetails = null;
    this.scanDetailsError = null;
    this.scanDetailsErrors = [];
    this.scannedResult = null;
    this.transactionFormVisible = false;
    this.transactionAmount = null;
    this.rewardSelectionVisible = false;
    this.rewards = [];
    this.rewardsError = null;
    this.manualCode = '';

    // Wait a moment to ensure camera is released before starting again
    if (!this.manualEntryMode) {
      setTimeout(() => this.startScanLoop(), 300);
    }
  }

  toggleManualEntry(): void {
    this.manualEntryMode = !this.manualEntryMode;
    if (this.manualEntryMode) {
      // Stop camera when switching to manual mode
      this.controls?.stop();
      this.controls = null;
      this.stopStream();
      this.scanning = false;
    } else {
      // Restart camera when switching back to scan mode
      this.manualCode = '';
      this.scanning = true;
      setTimeout(() => this.startScanLoop(), 300);
    }
  }

  submitManualCode(): void {
    const code = this.manualCode.trim();
    if (!code) return;
    this.scannedCode = code;
    this.scanning = false;
    this.sendToBackend(code);
  }

  get transactionLimits(): { min: number | null; max: number | null } {
    const raw = this.campaign?.limitation_for_transaction;
    if (!raw) return { min: null, max: null };
    const parts = raw.split(';');
    const min = parts[1] !== undefined && parts[1].trim() !== '' ? +parts[1] : null;
    const max = parts[2] !== undefined && parts[2].trim() !== '' ? +parts[2] : null;
    return { min, max };
  }

  get calculatedPoints(): number | null {
    if (!this.transactionAmount || !this.campaign?.point_per_transaction) return null;
    return Math.round(this.transactionAmount * this.campaign.point_per_transaction);
  }

  get entryLimits(): { count: number | null; period: string } {
    const raw = this.campaign?.limitation_for_entry;
    if (!raw) return { count: null, period: 'd' };
    const parts = raw.split(';');
    let count: number | null = null;
    let period = 'd';
    if (parts[0]) {
      const colonIdx = parts[0].indexOf(':');
      if (colonIdx > -1) {
        count = +parts[0].substring(0, colonIdx);
        const periodPart = parts[0].substring(colonIdx + 1); // e.g. '1d'
        period = periodPart.replace(/^1/, '') || 'd';
      } else {
        count = +parts[0];
      }
    }
    return { count, period };
  }

  get isCampaignValid(): boolean {
    if (!this.campaign) return false;
    if (!this.campaign.active) return false;
    if (this.campaign.valid) {
      return new Date(this.campaign.valid) >= new Date();
    }
    return true;
  }

  openRewardSelection(): void {
    if (!this.campaign?.partner_id) return;
    this.rewardSelectionVisible = true;
    this.rewardsLoading = true;
    this.rewardsError = null;
    this.partnerProductsService.getList({ partner_id: this.campaign.partner_id, limit: 100 }).subscribe({
      next: (res) => {
        this.rewards = (res.items || []).filter(p => (p.score ?? 0) > 0);
        this.rewardsLoading = false;
      },
      error: () => {
        this.rewardsError = 'Nie udało się załadować nagród.';
        this.rewardsLoading = false;
      }
    });
  }

  closeRewardSelection(): void {
    this.rewardSelectionVisible = false;
    this.rewards = [];
    this.rewardsError = null;
  }

  canAffordReward(product: PartnerProduct): boolean {
    return (this.scanDetails?.total_score ?? 0) >= (product.score ?? 0);
  }

  selectProduct(product: PartnerProduct): void {
    if (!this.canAffordReward(product)) return;
    this.selectedProduct = product;
    this.productPopupVisible = true;
    this.productCountdown = 6;
    this.productProcessing = false;
    this.productSuccess = false;
    this.productError = null;

    this.productCountdownInterval = setInterval(() => {
      this.productCountdown--;
      if (this.productCountdown <= 0) {
        clearInterval(this.productCountdownInterval);
        this.productCountdownInterval = null;
        this.submitProductRegistration();
      }
    }, 1000);
  }

  sendProductNow(): void {
    clearInterval(this.productCountdownInterval);
    this.productCountdownInterval = null;
    this.submitProductRegistration();
  }

  cancelProductPopup(): void {
    clearInterval(this.productCountdownInterval);
    this.productCountdownInterval = null;
    this.productPopupVisible = false;
    this.productSuccess = false;
    this.productError = null;
    this.selectedProduct = null;
  }

  private submitProductRegistration(): void {
    this.productProcessing = true;
    this.campaignScanService.processScanning({
      campaign_id: this.campaignId!,
      code: this.scannedCode!,
      type: 'PRODUCT',
      foreign_id: this.selectedProduct!.id
    }).subscribe({
      next: () => {
        this.productProcessing = false;
        this.productSuccess = true;
        navigator?.vibrate([200, 100, 200, 200, 400]);
        setTimeout(() => {
          this.cancelProductPopup();
          this.restartScan();
        }, 1500);
      },
      error: () => {
        this.productProcessing = false;
        this.productError = 'Wystąpił błąd podczas rejestracji nagrody.';
        navigator?.vibrate([500, 100, 500]);
      }
    });
  }

  registerTransaction(): void {
    this.transactionFormVisible = !this.transactionFormVisible;
    if (!this.transactionFormVisible) {
      this.transactionAmount = null;
    }
  }

  submitTransactionForm(): void {
    const { min, max } = this.transactionLimits;
    if (!this.transactionAmount || this.transactionAmount <= 0) return;
    if (min !== null && this.transactionAmount < min) return;
    if (max !== null && this.transactionAmount > max) return;
    this.transactionFormVisible = false;
    this.transactionPopupVisible = true;
    this.transactionCountdown = 10;
    this.transactionProcessing = false;
    this.transactionSuccess = false;
    this.transactionError = null;

    this.transactionCountdownInterval = setInterval(() => {
      this.transactionCountdown--;
      if (this.transactionCountdown <= 0) {
        clearInterval(this.transactionCountdownInterval);
        this.transactionCountdownInterval = null;
        this.submitTransactionRegistration();
      }
    }, 1000);
  }

  sendTransactionNow(): void {
    clearInterval(this.transactionCountdownInterval);
    this.transactionCountdownInterval = null;
    this.submitTransactionRegistration();
  }

  cancelTransactionPopup(): void {
    clearInterval(this.transactionCountdownInterval);
    this.transactionCountdownInterval = null;
    this.transactionPopupVisible = false;
    this.transactionSuccess = false;
    this.transactionError = null;
    this.transactionErrorDetail = null;
    this.transactionAmount = null;
  }

  private submitTransactionRegistration(): void {
    this.transactionProcessing = true;
    this.campaignScanService.processScanning({
      campaign_id: this.campaignId!,
      code: this.scannedCode!,
      amount: this.transactionAmount!,
      type: 'TRANSACTION'
    }).subscribe({
      next: () => {
        this.transactionProcessing = false;
        this.transactionSuccess = true;
        navigator?.vibrate([200, 100, 200, 200, 400]);
        setTimeout(() => {
          this.cancelTransactionPopup();
          this.restartScan();
        }, 1500);
      },
      error: (err: any) => {
        this.transactionProcessing = false;
        this.transactionError = 'Wystąpił błąd podczas rejestracji transakcji.';
        const body = err?.error;
        this.transactionErrorDetail =
          body?.detail ??
          body?.message ??
          body?.error ??
          null;
        navigator?.vibrate([500, 100, 500]);
      }
    });
  }

  registerEntry(): void {
    this.entryPopupVisible = true;
    this.entryCountdown = 10;
    this.entryProcessing = false;
    this.entrySuccess = false;
    this.entryError = null;

    this.entryCountdownInterval = setInterval(() => {
      this.entryCountdown--;
      if (this.entryCountdown <= 0) {
        clearInterval(this.entryCountdownInterval);
        this.entryCountdownInterval = null;
        this.submitEntryRegistration();
      }
    }, 1000);
  }

  sendEntryNow(): void {
    clearInterval(this.entryCountdownInterval);
    this.entryCountdownInterval = null;
    this.submitEntryRegistration();
  }

  cancelEntryPopup(): void {
    clearInterval(this.entryCountdownInterval);
    this.entryCountdownInterval = null;
    this.entryPopupVisible = false;
    this.entrySuccess = false;
    this.entryError = null;
  }

  private submitEntryRegistration(): void {
    this.entryProcessing = true;
    this.campaignScanService.processScanning({
      campaign_id: this.campaignId!,
      code: this.scannedCode!,
      type: 'ENTRY'
    }).subscribe({
      next: () => {
        this.entryProcessing = false;
        this.entrySuccess = true;
        navigator?.vibrate([200, 100, 200, 200, 400]);
        setTimeout(() => {
          this.cancelEntryPopup();
          this.restartScan();
        }, 1500);
      },
      error: () => {
        this.entryProcessing = false;
        this.entryError = 'Wystąpił błąd podczas rejestracji wejścia.';
        navigator?.vibrate([500, 100, 500]);
      }
    });
  }

}
